import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const measurementId = import.meta.env["VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY"];

let stubReady = false;
let scriptRequested = false;
let loadScheduled = false;

function ensureGtagStub() {
  if (typeof window === "undefined" || stubReady) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };
  stubReady = true;
}

function injectGoogleAnalyticsScript() {
  if (!measurementId || scriptRequested || typeof document === "undefined") return;
  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`)) {
    scriptRequested = true;
    return;
  }

  scriptRequested = true;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.gtag("js", new Date());
  window.gtag("config", measurementId);
}

export function initGoogleAnalytics() {
  if (!measurementId) {
    console.warn("Google Analytics measurement ID is not configured");
    return;
  }

  if (typeof window === "undefined") return;

  ensureGtagStub();
  if (loadScheduled) return;
  loadScheduled = true;

  const events = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
  let fallbackTimer = 0;

  const load = () => {
    if (fallbackTimer) window.clearTimeout(fallbackTimer);
    events.forEach((event) => window.removeEventListener(event, load));
    injectGoogleAnalyticsScript();
  };

  events.forEach((event) => window.addEventListener(event, load, { once: true, passive: true }));

  const startFallback = () => {
    fallbackTimer = window.setTimeout(load, 15000);
  };

  if (document.readyState === "complete") {
    startFallback();
  } else {
    window.addEventListener("load", startFallback, { once: true });
  }
}

export function trackEvent(eventName: string, params?: Record<string, string | number | boolean>) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", eventName, params ?? {});
}

export function useGoogleAnalyticsPageViews() {
  const router = useRouter();

  useEffect(() => {
    if (!measurementId) return;
    ensureGtagStub();

    const unsubscribe = router.subscribe("onResolved", () => {
      window.gtag("event", "page_view", {
        page_path: window.location.pathname,
        page_title: document.title,
      });
    });

    return () => unsubscribe();
  }, [router]);
}
